import React, { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { featureToggles } from './config/features';
import {
  REPAYMENTS_PER_YEAR,
  calculateAmortization,
  calculateLvr,
  estimateLmi,
  estimateStampDuty,
  formatAud,
} from './utils/mortgage';

const states = ['NSW', 'VIC', 'QLD'];

const FREQUENCY_LABELS = {
  monthly: 'Month',
  fortnightly: 'Fortnight',
  weekly: 'Week',
};

const FREQUENCY_DESCRIPTIONS = {
  monthly: 'Monthly',
  fortnightly: 'Fortnightly',
  weekly: 'Weekly',
};

const FREQUENCY_PERIOD_LABELS = {
  monthly: ['month', 'months'],
  fortnightly: ['fortnight', 'fortnights'],
  weekly: ['week', 'weeks'],
};

const DEFAULT_FORM_VALUES = {
  propertyValue: 875000,
  depositAmount: 175000,
  annualRate: 6.1,
  termYears: 30,
  frequency: 'fortnightly',
  offsetBalance: 15000,
  extraRepayment: 0,
  state: 'NSW',
  includeLmi: true,
};

const parseInputValue = (value) => (value === '' ? 0 : Number(value));

const getLoanAmount = (propertyValue, depositAmount) => Math.max(propertyValue - depositAmount, 0);

const getDepositRatio = (propertyValue, depositAmount) => {
  if (!Number.isFinite(propertyValue) || propertyValue <= 0) return 0;
  return (depositAmount / propertyValue) * 100;
};

const getValidationErrors = ({
  propertyValue,
  depositAmount,
  annualRate,
  termYears,
  frequency,
  state,
  offsetBalance,
  extraRepayment,
}) => {
  const errors = {};

  if (!Number.isFinite(propertyValue) || propertyValue <= 0) {
    errors.propertyValue = 'Enter a property value above $0.';
  }

  if (!Number.isFinite(depositAmount) || depositAmount < 0) {
    errors.depositAmount = 'Deposit cannot be negative.';
  } else if (Number.isFinite(propertyValue) && propertyValue > 0 && depositAmount >= propertyValue) {
    errors.depositAmount = 'Deposit must be less than the property value so a loan remains to calculate.';
  }

  if (!Number.isFinite(annualRate) || annualRate < 0 || annualRate > 20) {
    errors.annualRate = 'Use an interest rate between 0% and 20%.';
  }

  if (!Number.isFinite(termYears) || termYears <= 0 || termYears > 40) {
    errors.termYears = 'Choose a loan term between 1 and 40 years.';
  }

  if (!REPAYMENTS_PER_YEAR[frequency]) {
    errors.frequency = 'Select a supported repayment frequency.';
  }

  if (!states.includes(state)) {
    errors.state = 'Select NSW, VIC, or QLD.';
  }

  if (!Number.isFinite(offsetBalance) || offsetBalance < 0) {
    errors.offsetBalance = 'Offset balance cannot be negative.';
  }

  if (!Number.isFinite(extraRepayment) || extraRepayment < 0) {
    errors.extraRepayment = 'Extra repayments cannot be negative.';
  }

  return errors;
};

const getPayoffTimeline = (periods, frequency) => {
  const periodsPerYear = REPAYMENTS_PER_YEAR[frequency];
  const [singular, plural] = FREQUENCY_PERIOD_LABELS[frequency] ?? ['period', 'periods'];

  if (!periodsPerYear || periods <= 0) {
    return `0 ${plural}`;
  }

  const fullYears = Math.floor(periods / periodsPerYear);
  const remainder = periods % periodsPerYear;

  if (fullYears === 0) {
    return `${periods} ${periods === 1 ? singular : plural}`;
  }

  if (remainder === 0) {
    return `${fullYears} ${fullYears === 1 ? 'year' : 'years'}`;
  }

  return `${fullYears} ${fullYears === 1 ? 'year' : 'years'} ${remainder} ${remainder === 1 ? singular : plural}`;
};

const getFieldClasses = (hasError) =>
  `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 ${
    hasError ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-200'
  }`;

const workflowSteps = [
  {
    title: 'Start with the purchase',
    description: 'Enter the property price, deposit, and state so the calculator can derive your base loan and upfront cash.',
  },
  {
    title: 'Shape the loan',
    description: 'Set the interest rate, loan term, repayment frequency, and optional LMI, offset, or extra repayments.',
  },
  {
    title: 'Review the scenario',
    description: 'Calculate the repayment, compare strategy savings, and inspect the payoff timeline and balance chart.',
  },
];

const App = () => {
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [submittedValues, setSubmittedValues] = useState(DEFAULT_FORM_VALUES);

  const validationErrors = useMemo(() => getValidationErrors(formValues), [formValues]);
  const hasErrors = Object.keys(validationErrors).length > 0;

  const draftLoanAmount = useMemo(
    () => getLoanAmount(formValues.propertyValue, formValues.depositAmount),
    [formValues.propertyValue, formValues.depositAmount]
  );
  const draftDepositRatio = useMemo(
    () => getDepositRatio(formValues.propertyValue, formValues.depositAmount),
    [formValues.propertyValue, formValues.depositAmount]
  );
  const draftLvr = useMemo(
    () => calculateLvr(draftLoanAmount, formValues.propertyValue),
    [draftLoanAmount, formValues.propertyValue]
  );
  const draftStampDuty = useMemo(
    () => estimateStampDuty(formValues.propertyValue, formValues.state),
    [formValues.propertyValue, formValues.state]
  );
  const draftUpfrontCash = formValues.depositAmount + (featureToggles.showUpfrontCosts ? draftStampDuty : 0);

  const loanAmount = useMemo(
    () => getLoanAmount(submittedValues.propertyValue, submittedValues.depositAmount),
    [submittedValues.propertyValue, submittedValues.depositAmount]
  );
  const depositRatio = useMemo(
    () => getDepositRatio(submittedValues.propertyValue, submittedValues.depositAmount),
    [submittedValues.propertyValue, submittedValues.depositAmount]
  );
  const lvr = useMemo(() => calculateLvr(loanAmount, submittedValues.propertyValue), [loanAmount, submittedValues.propertyValue]);
  const estimatedLmi = useMemo(() => estimateLmi(loanAmount, lvr), [loanAmount, lvr]);
  const stampDuty = useMemo(
    () => estimateStampDuty(submittedValues.propertyValue, submittedValues.state),
    [submittedValues.propertyValue, submittedValues.state]
  );

  const appliedOffsetBalance = featureToggles.showOffsetAccount ? submittedValues.offsetBalance : 0;
  const appliedExtraRepayment = featureToggles.showExtraRepayment ? submittedValues.extraRepayment : 0;
  const principal = loanAmount + (submittedValues.includeLmi ? estimatedLmi : 0);

  const summary = useMemo(
    () =>
      calculateAmortization({
        loanAmount: principal,
        annualRate: submittedValues.annualRate,
        years: submittedValues.termYears,
        frequency: submittedValues.frequency,
        offsetBalance: appliedOffsetBalance,
        extraRepayment: appliedExtraRepayment,
      }),
    [
      principal,
      submittedValues.annualRate,
      submittedValues.termYears,
      submittedValues.frequency,
      appliedOffsetBalance,
      appliedExtraRepayment,
    ]
  );

  const baselineSummary = useMemo(
    () =>
      calculateAmortization({
        loanAmount: principal,
        annualRate: submittedValues.annualRate,
        years: submittedValues.termYears,
        frequency: submittedValues.frequency,
        offsetBalance: 0,
        extraRepayment: 0,
      }),
    [principal, submittedValues.annualRate, submittedValues.termYears, submittedValues.frequency]
  );

  const interestSaved = Math.max(0, baselineSummary.totalInterest - summary.totalInterest);
  const periodsSaved = Math.max(0, baselineSummary.periods - summary.periods);
  const upfrontCash = submittedValues.depositAmount + (featureToggles.showUpfrontCosts ? stampDuty : 0);

  const scenarioHighlights = useMemo(() => {
    const notes = [];

    if (depositRatio < 20) {
      notes.push('Your deposit is below 20%, so this scenario is likely to trigger LMI.');
    } else {
      notes.push('Your deposit is at or above 20%, which avoids estimated LMI in this calculator.');
    }

    if (lvr > 80 && submittedValues.includeLmi) {
      notes.push('Estimated LMI has been capitalised into the financed loan because the LVR is above 80%.');
    } else if (lvr > 80) {
      notes.push('Estimated LMI is shown separately because you chose not to capitalise it into the loan.');
    }

    if (interestSaved > 0 || periodsSaved > 0) {
      notes.push(
        `Your repayment strategy saves about ${formatAud(interestSaved)} in interest and trims ${getPayoffTimeline(
          periodsSaved,
          submittedValues.frequency
        )} off the loan term.`
      );
    } else {
      notes.push('No offset or extra repayment savings are applied in the current submitted scenario.');
    }

    return notes;
  }, [depositRatio, lvr, submittedValues.includeLmi, interestSaved, periodsSaved, submittedValues.frequency]);

  const handleNumberChange = (field) => (event) => {
    const value = parseInputValue(event.target.value);
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleSelectChange = (field) => (event) => {
    const value = event.target.value;
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleCheckboxChange = (field) => (event) => {
    const value = event.target.checked;
    setFormValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (hasErrors) return;
    setSubmittedValues({ ...formValues });
  };

  const handleReset = () => {
    setFormValues({ ...DEFAULT_FORM_VALUES });
    setSubmittedValues({ ...DEFAULT_FORM_VALUES });
  };

  const actionMessage = hasErrors
    ? 'Fix the highlighted fields before calculating your repayment scenario.'
    : 'Press Calculate repayments to refresh the results using the current inputs.';

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="rounded-3xl bg-slate-900 px-6 py-8 text-white shadow-xl sm:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-200">Mortgage planning workflow</p>
              <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">Australian Mortgage Calculator</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
                Plan a purchase, shape the loan, and review a smarter repayment scenario with deposit, LVR, LMI,
                stamp duty, offset, and extra-repayment insights in one place.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
              <p className="font-semibold text-white">Current workflow</p>
              <p className="mt-1">Enter your scenario, calculate it explicitly, then use the summary and chart to refine the plan.</p>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {workflowSteps.map((step, index) => (
              <div key={step.title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Step {index + 1}</p>
                <h2 className="mt-2 text-lg font-semibold text-white">{step.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-200">{step.description}</p>
              </div>
            ))}
          </div>
        </header>

        <main className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <section className="rounded-3xl bg-white p-6 shadow-lg shadow-slate-200/70 sm:p-8">
            <div className="flex flex-col gap-2 border-b border-slate-100 pb-6">
              <h2 className="text-2xl font-semibold">Build your scenario</h2>
              <p className="text-sm leading-6 text-slate-600">
                The form is organised around the same decisions borrowers make in practice: purchase details first, then loan
                settings, then optional repayment strategy inputs.
              </p>
            </div>

            <form className="mt-8 space-y-8" onSubmit={handleSubmit}>
              <fieldset className="space-y-5">
                <legend className="text-lg font-semibold text-slate-900">Purchase details</legend>
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="propertyValue">
                      Property value (AUD)
                    </label>
                    <input
                      id="propertyValue"
                      aria-describedby="propertyValue-hint propertyValue-error"
                      aria-invalid={Boolean(validationErrors.propertyValue)}
                      className={getFieldClasses(Boolean(validationErrors.propertyValue))}
                      min="0"
                      step="1000"
                      type="number"
                      value={formValues.propertyValue}
                      onChange={handleNumberChange('propertyValue')}
                    />
                    <p className="mt-2 text-xs text-slate-500" id="propertyValue-hint">
                      Enter the purchase price you want to model.
                    </p>
                    {validationErrors.propertyValue && (
                      <p className="mt-2 text-sm text-red-600" id="propertyValue-error">
                        {validationErrors.propertyValue}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="depositAmount">
                      Deposit (AUD)
                    </label>
                    <input
                      id="depositAmount"
                      aria-describedby="depositAmount-hint depositAmount-error"
                      aria-invalid={Boolean(validationErrors.depositAmount)}
                      className={getFieldClasses(Boolean(validationErrors.depositAmount))}
                      min="0"
                      step="1000"
                      type="number"
                      value={formValues.depositAmount}
                      onChange={handleNumberChange('depositAmount')}
                    />
                    <p className="mt-2 text-xs text-slate-500" id="depositAmount-hint">
                      This calculator derives the loan amount from price minus deposit.
                    </p>
                    {validationErrors.depositAmount && (
                      <p className="mt-2 text-sm text-red-600" id="depositAmount-error">
                        {validationErrors.depositAmount}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700" htmlFor="state">
                      State (stamp duty)
                    </label>
                    <select
                      id="state"
                      aria-describedby="state-hint state-error"
                      aria-invalid={Boolean(validationErrors.state)}
                      className={getFieldClasses(Boolean(validationErrors.state))}
                      value={formValues.state}
                      onChange={handleSelectChange('state')}
                    >
                      {states.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-slate-500" id="state-hint">
                      Stamp duty is estimated using the current NSW, VIC, or QLD rules in this app.
                    </p>
                    {validationErrors.state && (
                      <p className="mt-2 text-sm text-red-600" id="state-error">
                        {validationErrors.state}
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Live purchase snapshot</p>
                      <p className="text-sm text-slate-600">These figures update while you type so you can see the shape of the scenario before submitting it.</p>
                    </div>
                    <p className="text-sm font-semibold text-blue-700">Deposit ratio: {draftDepositRatio.toFixed(1)}%</p>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Loan amount to finance</p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{formatAud(draftLoanAmount)}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {featureToggles.showUpfrontCosts ? 'Estimated stamp duty' : 'Estimated LVR'}
                      </p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">
                        {featureToggles.showUpfrontCosts ? formatAud(draftStampDuty) : `${draftLvr.toFixed(2)}%`}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {featureToggles.showUpfrontCosts ? 'Estimated upfront cash' : 'Deposit committed'}
                      </p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{formatAud(draftUpfrontCash)}</p>
                    </div>
                  </div>
                </div>
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-lg font-semibold text-slate-900">Loan settings</legend>
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="annualRate">
                      Interest rate (%)
                    </label>
                    <input
                      id="annualRate"
                      aria-describedby="annualRate-hint annualRate-error"
                      aria-invalid={Boolean(validationErrors.annualRate)}
                      className={getFieldClasses(Boolean(validationErrors.annualRate))}
                      min="0"
                      max="20"
                      step="0.01"
                      type="number"
                      value={formValues.annualRate}
                      onChange={handleNumberChange('annualRate')}
                    />
                    <p className="mt-2 text-xs text-slate-500" id="annualRate-hint">
                      Use the annual interest rate offered by the lender.
                    </p>
                    {validationErrors.annualRate && (
                      <p className="mt-2 text-sm text-red-600" id="annualRate-error">
                        {validationErrors.annualRate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="termYears">
                      Loan term (years)
                    </label>
                    <input
                      id="termYears"
                      aria-describedby="termYears-hint termYears-error"
                      aria-invalid={Boolean(validationErrors.termYears)}
                      className={getFieldClasses(Boolean(validationErrors.termYears))}
                      min="1"
                      max="40"
                      step="1"
                      type="number"
                      value={formValues.termYears}
                      onChange={handleNumberChange('termYears')}
                    />
                    <p className="mt-2 text-xs text-slate-500" id="termYears-hint">
                      Typical Australian loans run from 25 to 30 years.
                    </p>
                    {validationErrors.termYears && (
                      <p className="mt-2 text-sm text-red-600" id="termYears-error">
                        {validationErrors.termYears}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700" htmlFor="frequency">
                      Repayment frequency
                    </label>
                    <select
                      id="frequency"
                      aria-describedby="frequency-hint frequency-error"
                      aria-invalid={Boolean(validationErrors.frequency)}
                      className={getFieldClasses(Boolean(validationErrors.frequency))}
                      value={formValues.frequency}
                      onChange={handleSelectChange('frequency')}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="weekly">Weekly</option>
                    </select>
                    <p className="mt-2 text-xs text-slate-500" id="frequency-hint">
                      Choose how often you want the repayment amount shown.
                    </p>
                    {validationErrors.frequency && (
                      <p className="mt-2 text-sm text-red-600" id="frequency-error">
                        {validationErrors.frequency}
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <label className="flex items-start gap-3 text-sm text-slate-700" htmlFor="includeLmi">
                      <input
                        checked={formValues.includeLmi}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        id="includeLmi"
                        type="checkbox"
                        onChange={handleCheckboxChange('includeLmi')}
                      />
                      <span>
                        <span className="block font-medium text-slate-900">Capitalise estimated LMI into loan</span>
                        <span className="mt-1 block text-xs text-slate-500">
                          Turn this on to add the estimated LMI premium to the financed balance instead of treating it separately.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-lg font-semibold text-slate-900">Repayment strategy</legend>
                <p className="text-sm leading-6 text-slate-600">
                  Use these optional fields to model how cash reserves and extra repayments can reduce interest and shorten the term.
                </p>

                <div className="grid gap-5 md:grid-cols-2">
                  {featureToggles.showOffsetAccount && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700" htmlFor="offsetBalance">
                        Offset balance (AUD)
                      </label>
                      <input
                        id="offsetBalance"
                        aria-describedby="offsetBalance-hint offsetBalance-error"
                        aria-invalid={Boolean(validationErrors.offsetBalance)}
                        className={getFieldClasses(Boolean(validationErrors.offsetBalance))}
                        min="0"
                        step="1000"
                        type="number"
                        value={formValues.offsetBalance}
                        onChange={handleNumberChange('offsetBalance')}
                      />
                      <p className="mt-2 text-xs text-slate-500" id="offsetBalance-hint">
                        Offset funds reduce the balance used for interest calculations in this model.
                      </p>
                      {validationErrors.offsetBalance && (
                        <p className="mt-2 text-sm text-red-600" id="offsetBalance-error">
                          {validationErrors.offsetBalance}
                        </p>
                      )}
                    </div>
                  )}

                  {featureToggles.showExtraRepayment && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700" htmlFor="extraRepayment">
                        Extra repayment per period (AUD)
                      </label>
                      <input
                        id="extraRepayment"
                        aria-describedby="extraRepayment-hint extraRepayment-error"
                        aria-invalid={Boolean(validationErrors.extraRepayment)}
                        className={getFieldClasses(Boolean(validationErrors.extraRepayment))}
                        min="0"
                        step="50"
                        type="number"
                        value={formValues.extraRepayment}
                        onChange={handleNumberChange('extraRepayment')}
                      />
                      <p className="mt-2 text-xs text-slate-500" id="extraRepayment-hint">
                        This is added on top of the minimum repayment for the chosen frequency.
                      </p>
                      {validationErrors.extraRepayment && (
                        <p className="mt-2 text-sm text-red-600" id="extraRepayment-error">
                          {validationErrors.extraRepayment}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </fieldset>

              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Calculate when you are ready</p>
                    <p className="mt-1 text-sm text-slate-600">{actionMessage}</p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      disabled={hasErrors}
                      type="submit"
                    >
                      Calculate repayments
                    </button>
                    <button
                      className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      type="button"
                      onClick={handleReset}
                    >
                      Clear form
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </section>

          <section className="space-y-6">
            <div className="rounded-3xl bg-slate-900 p-6 text-white shadow-xl sm:p-8">
              <div className="flex flex-col gap-5 border-b border-white/10 pb-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-200">Submitted scenario</p>
                    <h2 className="mt-2 text-2xl font-semibold">Repayment summary</h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                      Based on a {formatAud(submittedValues.propertyValue)} property, {formatAud(submittedValues.depositAmount)} deposit,
                      {` ${submittedValues.annualRate.toFixed(2)}%`} rate, and a {submittedValues.termYears}-year term.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-blue-500/10 px-4 py-3 text-right">
                    <p className="text-xs font-medium uppercase tracking-wide text-blue-200">
                      Repayment per {FREQUENCY_LABELS[submittedValues.frequency] ?? submittedValues.frequency}
                    </p>
                    <p className="mt-2 text-3xl font-bold">{formatAud(summary.repayment)}</p>
                    <p className="mt-2 text-sm text-slate-300">{FREQUENCY_DESCRIPTIONS[submittedValues.frequency]} repayment view</p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">Loan amount</p>
                    <p className="mt-2 text-xl font-semibold">{formatAud(loanAmount)}</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">Deposit ratio</p>
                    <p className="mt-2 text-xl font-semibold">{depositRatio.toFixed(1)}%</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">Estimated LVR</p>
                    <p className="mt-2 text-xl font-semibold">{lvr.toFixed(2)}%</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-300">Time to repay</p>
                    <p className="mt-2 text-xl font-semibold">{getPayoffTimeline(summary.periods, submittedValues.frequency)}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/5 p-5">
                  <h3 className="text-lg font-semibold text-white">Loan costs</h3>
                  <dl className="mt-4 space-y-3 text-sm text-slate-200">
                    <div className="flex items-center justify-between gap-4">
                      <dt>Estimated LMI</dt>
                      <dd className="font-semibold text-white">{formatAud(estimatedLmi)}</dd>
                    </div>
                    {featureToggles.showUpfrontCosts && (
                      <div className="flex items-center justify-between gap-4">
                        <dt>Estimated Stamp Duty ({submittedValues.state})</dt>
                        <dd className="font-semibold text-white">{formatAud(stampDuty)}</dd>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                      <dt>{submittedValues.includeLmi ? 'Financed loan including LMI' : 'Financed loan without LMI'}</dt>
                      <dd className="font-semibold text-white">{formatAud(principal)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <dt>{featureToggles.showUpfrontCosts ? 'Upfront cash needed' : 'Deposit committed'}</dt>
                      <dd className="font-semibold text-white">{formatAud(upfrontCash)}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-2xl bg-blue-500/10 p-5">
                  <h3 className="text-lg font-semibold text-white">Outcome</h3>
                  <dl className="mt-4 space-y-3 text-sm text-slate-200">
                    <div className="flex items-center justify-between gap-4">
                      <dt>Total interest</dt>
                      <dd className="font-semibold text-white">{formatAud(summary.totalInterest)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <dt>Total paid</dt>
                      <dd className="font-semibold text-white">{formatAud(summary.totalPaid)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <dt>Interest saved vs base loan</dt>
                      <dd className="font-semibold text-white">{formatAud(interestSaved)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <dt>Term saved vs base loan</dt>
                      <dd className="font-semibold text-white">{getPayoffTimeline(periodsSaved, submittedValues.frequency)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-lg shadow-slate-200/70 sm:p-8">
              <h2 className="text-2xl font-semibold">Smart scenario guidance</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                These notes translate the raw numbers into the main decision points borrowers usually care about.
              </p>
              <div className="mt-5 grid gap-4">
                {scenarioHighlights.map((note) => (
                  <div key={note} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                    {note}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-lg shadow-slate-200/70 sm:p-8">
              <div className="flex flex-col gap-2 border-b border-slate-100 pb-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold">Balance over time</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Use the chart to see how quickly the submitted scenario pays the loan down.
                  </p>
                </div>
                <p className="text-sm font-medium text-slate-500">Schedule points: {summary.schedule.length}</p>
              </div>

              <div className="mt-6 h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={summary.schedule.map((entry) => ({ period: entry.period, balance: entry.balance }))}>
                    <CartesianGrid stroke="#cbd5e1" strokeDasharray="3 3" />
                    <XAxis dataKey="period" stroke="#475569" />
                    <YAxis stroke="#475569" tickFormatter={(value) => `${Math.round(value / 1000)}k`} />
                    <Tooltip
                      formatter={(value) => formatAud(Number(value))}
                      labelFormatter={(value) => `Period ${value}`}
                    />
                    <Line dataKey="balance" dot={false} stroke="#2563eb" strokeWidth={3} type="monotone" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default App;
